import { zodResolver } from '@hookform/resolvers/zod'
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { supabase } from '@/integrations/supabase/client'
import { useAuth } from '@/lib/AuthProvider'
import { type OnboardingData, onboardingSchema } from '@/lib/validations'

export const Route = createFileRoute('/onboarding')({
  beforeLoad: ({ context }) => {
    if (context.auth && !context.auth.isLoading) {
      if (!context.auth.user) {
        throw redirect({ to: '/login' })
      }
      // TEMPORÁRIO: Comentado para permitir que você teste visualmente
      // else if (context.auth.hasOnboarded) {
      //  throw redirect({ to: '/app' })
      // }
    }
  },
  component: Onboarding,
})

function Onboarding() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errorMsg, setErrorMsg] = useState('')

  const form = useForm<OnboardingData>({
    resolver: zodResolver(onboardingSchema),
    defaultValues: {
      fullName: '',
      document: '',
    },
  })

  const onSubmit = async (data: OnboardingData) => {
    if (!user) return
    setIsSubmitting(true)
    setErrorMsg('')

    try {
      // Tenta criar o registro de company (Lava-Jato)
      // Usando o CPF/CNPJ. Se o banco tiver unique partial index,
      // ele falhará se já houver esse documento cadastrado ativamente.
      const documentClean = data.document.replace(/\D/g, '')

      const { data: company, error: companyError } = await supabase
        .from('company_settings')
        .insert({
          name: `Lava-Jato de ${data.fullName}`,
          cnpj: documentClean,
        })
        .select()
        .single()

      if (companyError) {
        if (companyError.code === '23505') {
          throw new Error(
            'Este CPF/CNPJ já está cadastrado em nossa plataforma. Entre em contato com o suporte ou faça login com a conta existente.'
          )
        }
        throw companyError
      }

      const { error: profileError } = await supabase.from('profiles').insert({
        id: user.id,
        company_id: company.id,
        name: data.fullName,
        role: 'admin',
        status: 'active',
      })

      if (profileError) throw profileError

      // Verifica se existe intenção de compra direta via URL (ex: ?plan=pro)
      const searchParams = new URLSearchParams(window.location.search)
      const planToBuy = searchParams.get('plan')

      if (planToBuy && ['basico', 'pro', 'premium'].includes(planToBuy)) {
        // Fluxo de Compra Direta
        try {
          const res = await supabase.functions.invoke('asaas-checkout', {
            body: {
              planId: planToBuy,
              companyId: company.id,
              email: user.email,
              name: data.fullName,
              cpfCnpj: documentClean,
            },
          })

          if (res.error) throw new Error(res.error.message || 'Erro ao gerar fatura Asaas.')

          if (res.data?.checkoutUrl) {
            window.location.href = res.data.checkoutUrl
            return
          }
        } catch (checkoutErr: any) {
          console.error('Erro no checkout direto', checkoutErr)
          // Fallback silencioso para o Trial caso a API do Asaas falhe agora
          await darTrial(company.id)
        }
      } else {
        // Fluxo Normal (Trial)
        await darTrial(company.id)
      }

      // Sucesso! Redireciona para o app
      navigate({ to: '/app' })
    } catch (err: any) {
      setErrorMsg(err.message || 'Erro ao salvar os dados')
    } finally {
      setIsSubmitting(false)
    }
  }

  const darTrial = async (companyId: string) => {
    // Conceder Trial de 7 Dias automaticamente
    const trialEndDate = new Date()
    trialEndDate.setDate(trialEndDate.getDate() + 7)

    const { error: subError } = await supabase.from('company_subscriptions').insert({
      company_id: companyId,
      plan_id: 'premium',
      status: 'TRIALING',
      current_period_end: trialEndDate.toISOString(),
    })

    if (subError) console.error('Erro ao criar trial', subError)
  }

  // Máscara simples para CPF/CNPJ no input
  const handleDocumentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value.replace(/\D/g, '')
    if (value.length <= 11) {
      // CPF
      value = value.replace(/(\d{3})(\d)/, '$1.$2')
      value = value.replace(/(\d{3})(\d)/, '$1.$2')
      value = value.replace(/(\d{3})(\d{1,2})$/, '$1-$2')
    } else {
      // CNPJ
      value = value.substring(0, 14)
      value = value.replace(/^(\d{2})(\d)/, '$1.$2')
      value = value.replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
      value = value.replace(/\.(\d{3})(\d)/, '.$1/$2')
      value = value.replace(/(\d{4})(\d)/, '$1-$2')
    }
    form.setValue('document', value, { shouldValidate: true })
  }

  return (
    <div className="min-h-screen bg-black flex flex-col items-center justify-center p-4">
      <Card className="w-full max-w-md shadow-2xl border-primary/20">
        <CardHeader className="space-y-1 text-center">
          <CardTitle className="text-2xl font-bold">Bem-vindo(a)!</CardTitle>
          <CardDescription>
            Precisamos de mais alguns dados para liberar seu acesso ao AutoWash Pro V2.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            {errorMsg && (
              <div className="p-3 text-sm text-destructive bg-destructive/10 rounded-md">
                {errorMsg}
              </div>
            )}

            <div className="space-y-2 text-left">
              <Label htmlFor="fullName">Nome Completo do Responsável</Label>
              <Input
                id="fullName"
                placeholder="Seu nome completo"
                {...form.register('fullName')}
                className={form.formState.errors.fullName ? 'border-destructive' : ''}
              />
              {form.formState.errors.fullName && (
                <p className="text-xs text-destructive">{form.formState.errors.fullName.message}</p>
              )}
            </div>

            <div className="space-y-2 text-left">
              <Label htmlFor="document">CPF ou CNPJ</Label>
              <Input
                id="document"
                placeholder="000.000.000-00"
                {...form.register('document')}
                onChange={handleDocumentChange}
                className={form.formState.errors.document ? 'border-destructive' : ''}
              />
              {form.formState.errors.document && (
                <p className="text-xs text-destructive">{form.formState.errors.document.message}</p>
              )}
            </div>

            <div className="pt-2">
              <Button type="submit" className="w-full" disabled={isSubmitting}>
                {isSubmitting ? 'Validando...' : 'Completar Cadastro'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}
